import type { DatesParam } from '~/utils/datetime'

export type FormatterOptions = {
  locale?: string
  timezone?: string
}

/**
 * Format a date as a date ISO.
 *
 * @param date The date to format.
 * @param options The options to format the date in.
 * @returns The formatted date.
 *
 * @example formatAsDateISO('2021-01-01', { locale: 'en', timezone: 'UTC' }) // 2021-01-01
 * @example formatAsDateISO('2021-01-01', { locale: 'fr', timezone: 'America/New_York' }) // 2021-01-01
 */
export function formatAsDateISO(date?: DatesParam, { locale = 'en', timezone = 'UTC' }: FormatterOptions = {}) {
  return datetime.utc(date).locale(locale).tz(timezone).format('YYYY-MM-DD')
}

/**
 * Format a date as a date and month.
 *
 * @param date The date to format.
 * @param options The options to format the date in.
 * @returns The formatted date.
 *
 * @example formatAsDateMonth('2021-01-01', { locale: 'en', timezone: 'UTC' }) // Jan 1
 * @example formatAsDateMonth('2021-01-01', { locale: 'fr', timezone: 'America/New_York' }) // 1 janv.
 */
export function formatAsDateMonth(date?: DatesParam, { locale = 'en', timezone = 'UTC' }: FormatterOptions = {}) {
  const formatter = locale === 'en' ? 'MMM D' : 'D MMM'
  return datetime.utc(date).locale(locale).tz(timezone).format(formatter)
}

/**
 * Format a date as a short date with time.
 *
 * @param date The date to format.
 * @param options The options to format the date in.
 * @returns The formatted date.
 *
 * @example formatAsDateMonthWithTime('2021-01-01', { locale: 'en', timezone: 'UTC' }) // Jan 1, 12:00 AM
 * @example formatAsDateMonthWithTime('2021-01-01', { locale: 'fr', timezone: 'America/New_York' }) // 1 janv., 00:00
 */
export function formatAsDateMonthWithTime(date?: DatesParam, { locale = 'en', timezone = 'UTC' }: FormatterOptions = {}) {
  const formatter = locale === 'en' ? 'MMM D, h:mm A' : 'D MMM, HH:mm'
  return datetime.utc(date).locale(locale).tz(timezone).format(formatter)
}

/**
 * Format a date as a day and month.
 *
 * @param date The date to format.
 * @param options The options to format the date in.
 * @returns The formatted date.
 *
 * @example formatAsDayAndMonth('2021-01-01', { locale: 'en', timezone: 'UTC' }) // Friday, Jan 1
 * @example formatAsDayAndMonth('2021-01-01', { locale: 'fr', timezone: 'America/New_York' }) // vendredi, 1 janv.
 */
export function formatAsDayAndMonth(date?: DatesParam, { locale = 'en', timezone = 'UTC' }: FormatterOptions = {}) {
  const formatter = locale === 'en' ? 'dddd, MMM D' : 'dddd, D MMM'
  return datetime.utc(date).locale(locale).tz(timezone).format(formatter)
}

/**
 * Format a date as a day of the week.
 *
 * @param date The date to format.
 * @param options The options to format the date in.
 * @returns The formatted date.
 *
 * @example formatAsDayOfWeek('2021-01-01', { locale: 'en', timezone: 'UTC' }) // Friday
 * @example formatAsDayOfWeek('2021-01-01', { locale: 'fr', timezone: 'America/New_York' }) // vendredi
 */
export function formatAsDayOfWeek(date?: DatesParam, { locale = 'en', timezone = 'UTC' }: FormatterOptions = {}) {
  return datetime.utc(date).locale(locale).tz(timezone).format('dddd')
}

/**
 * Format a date as a full date long.
 *
 * @param date The date to format.
 * @param options The options to format the date in.
 * @returns The formatted date.
 *
 * @example formatAsFullDateLong('2021-01-01', { locale: 'en', timezone: 'UTC' }) // January 1, 2021
 * @example formatAsFullDateLong('2021-01-01', { locale: 'fr', timezone: 'America/New_York' }) // 1 janvier 2021
 */
export function formatAsFullDateLong(date?: DatesParam, { locale = 'en', timezone = 'UTC' }: FormatterOptions = {}) {
  return datetime.utc(date).locale(locale).tz(timezone).format('LL')
}

/**
 * Format a date as a 12 hour abbreviated time.
 *
 * @param date The date to format.
 * @param options The options to format the date in.
 * @returns The formatted date.
 *
 * @example formatAsHour12Abbreviated('2021-01-01', { locale: 'en', timezone: 'UTC' }) // 12am
 * @example formatAsHour12Abbreviated('2021-01-01', { locale: 'fr', timezone: 'America/New_York' }) // 12am
 */
export function formatAsHour12Abbreviated(date?: DatesParam, { locale = 'en', timezone = 'UTC' }: FormatterOptions = {}) {
  return datetime.utc(date).locale(locale).tz(timezone).format('ha')
}

/**
 * Format a date as an hour in 24 hour format.
 *
 * @param date The date to format.
 * @param options The options to format the date in.
 * @returns The formatted date.
 *
 * @example formatAsHour24('2021-01-01', { locale: 'en', timezone: 'UTC' }) // 0
 * @example formatAsHour24('2021-01-01', { locale: 'fr', timezone: 'America/New_York' }) // 0
 */
export function formatAsHour24(date?: DatesParam, { locale = 'en', timezone = 'UTC' }: FormatterOptions = {}) {
  return datetime.utc(date).locale(locale).tz(timezone).format('H')
}

/**
 * Format a date as an ISO string.
 *
 * @param date The date to format.
 * @param options The options to format the date in.
 * @returns The formatted date.
 *
 * @example formatAsISOString('2021-01-01', { locale: 'en', timezone: 'UTC' }) // 2021-01-01T12:00:00.000Z
 * @example formatAsISOString('2021-01-01', { locale: 'fr', timezone: 'America/New_York' }) // 2021-01-01T12:00:00.000Z
 */
export function formatAsISOString(date?: DatesParam, { locale = 'en', timezone = 'UTC' }: FormatterOptions = {}) {
  return datetime.utc(date).locale(locale).tz(timezone).toISOString()
}

/**
 * Format a date as a month and year.
 *
 * @param date The date to format.
 * @param options The options to format the date in.
 * @returns The formatted date.
 *
 * @example formatAsMonthYear('2021-01-01', { locale: 'en', timezone: 'UTC' }) // Jan 2021
 * @example formatAsMonthYear('2021-01-01', { locale: 'fr', timezone: 'America/New_York' }) // janv. 2021
 */
export function formatAsMonthYear(date?: DatesParam, { locale = 'en', timezone = 'UTC' }: FormatterOptions = {}) {
  return datetime.utc(date).locale(locale).tz(timezone).format('MMM YYYY')
}

/**
 * Short localized month label for a 1-based calendar month (e.g. API `Month` 1–12).
 *
 * Uses a fixed UTC anchor so the label does not shift across timezones.
 *
 * @param monthNumber Calendar month, 1 = January through 12 = December.
 * @param options Locale only; `timezone` is ignored.
 *
 * @example formatAsMonthName(3, { locale: 'en' }) // Mar
 * @example formatAsMonthName(3, { locale: 'fr' }) // mars
 */
export function formatAsMonthName(monthNumber: number, { locale = 'en' }: Pick<FormatterOptions, 'locale'> = {}) {
  const padded = String(monthNumber).padStart(2, '0')
  return datetime.utc(`2000-${padded}-01`).locale(locale).format('MMM')
}

/**
 * Format a date as a full month name and year.
 *
 * @param date The date to format.
 * @param options The options to format the date in.
 * @returns The formatted date.
 *
 * @example formatAsFullMonthYear('2021-01-01', { locale: 'en', timezone: 'UTC' }) // January 2021
 * @example formatAsFullMonthYear('2021-01-01', { locale: 'fr', timezone: 'America/New_York' }) // janvier 2021
 */
export function formatAsFullMonthYear(date?: DatesParam, { locale = 'en', timezone = 'UTC' }: FormatterOptions = {}) {
  return datetime.utc(date).locale(locale).tz(timezone).format('MMMM YYYY')
}

/**
 * Format a date as an ordinal date long.
 *
 * @param date The date to format.
 * @param options The options to format the date in.
 * @returns The formatted date.
 *
 * @example formatAsOrdinalDateLong('2021-01-01', { locale: 'en', timezone: 'UTC' }) // January 1st 2021
 * @example formatAsOrdinalDateLong('2021-01-01', { locale: 'fr', timezone: 'America/New_York' }) // 1er janvier 2021
 */
export function formatAsOrdinalDateLong(date?: DatesParam, { locale = 'en', timezone = 'UTC' }: FormatterOptions = {}) {
  const formatter = locale === 'en' ? 'MMMM Do YYYY' : 'Do MMMM YYYY'
  return datetime.utc(date).locale(locale).tz(timezone).format(formatter)
}

/**
 * Format a date as a short date.
 *
 * @param date The date to format.
 * @param options The options to format the date in.
 * @returns The formatted date.
 *
 * @example formatAsShortDate('2021-01-01', { locale: 'en', timezone: 'UTC' }) // Jan 1
 * @example formatAsShortDate('2021-01-01', { locale: 'fr', timezone: 'America/New_York' }) // 1 janv.
 */
export function formatAsShortDate(date?: DatesParam, { locale = 'en', timezone = 'UTC' }: FormatterOptions = {}) {
  const formatter = locale === 'en' ? 'MMM D' : 'D MMM'
  return datetime.utc(date).locale(locale).tz(timezone).format(formatter)
}

/**
 * Format a date as a short date range.
 *
 * @param startDate The start date.
 * @param endDate The end date.
 * @param options The options to format the date in.
 * @returns The formatted date range.
 *
 * @example formatAsShortDateRange('2021-01-01', '2021-01-02', { locale: 'en', timezone: 'UTC' }) // Jan 1 - 2
 * @example formatAsShortDateRange('2021-01-31', '2021-02-02', { locale: 'en', timezone: 'UTC' }) // Jan 31 - Feb 1
 * @example formatAsShortDateRange('2021-01-01', '2021-01-02', { locale: 'fr', timezone: 'America/New_York' }) // 31 janv. - 1 févr.
 * @example formatAsShortDateRange('2021-01-31', '2021-02-02', { locale: 'fr', timezone: 'America/New_York' }) // 31 janv. - 2 févr.
 */
export function formatAsShortDateRange(startDate: DatesParam, endDate: DatesParam, { locale = 'en', timezone = 'UTC' }: FormatterOptions = {}) {
  return `${formatAsShortDate(startDate, { locale, timezone })} - ${formatAsShortDate(endDate, { locale, timezone })}`
}

/**
 * Format as a short date range with year.
 *
 * @param startDate The start date.
 * @param endDate The end date.
 * @param options The options to format the date in.
 * @returns The formatted date range.
 *
 * @example formatAsShortDateRangeWithYear('2021-01-01', '2021-01-02', { locale: 'en', timezone: 'UTC' }) // Jan 1 - Jan 2, 2021
 * @example formatAsShortDateRangeWithYear('2020-12-01', '2021-01-02', { locale: 'en', timezone: 'UTC' }) // Dec 1, 2020 - Jan 2, 2021
 * @example formatAsShortDateRangeWithYear('2021-01-01', '2021-01-02', { locale: 'fr', timezone: 'America/New_York' }) // 1 janv. - 2 janv., 2021
 * @example formatAsShortDateRangeWithYear('2020-12-01', '2021-01-02', { locale: 'fr', timezone: 'America/New_York' }) // 1 déc. 2020 - 2 janv., 2021
 *
 * @todo: where this method is implemented, provide the RV timezone in the options.
 */
export function formatAsShortDateRangeWithYear(startDate: DatesParam, endDate: DatesParam, { locale = 'en', timezone = 'UTC' }: FormatterOptions = {}) {
  if (datetime.utc(startDate).isSame(datetime.utc(endDate), 'year')) {
    return `${formatAsDateMonth(startDate, { locale, timezone })} - ${formatAsShortDateYear(endDate, { locale, timezone })}`
  }
  return formatAsShortDateRangeWithYears(startDate, endDate, { locale, timezone })
}

/**
 * Format a date as a short date range with year.
 *
 * @param startDate The start date.
 * @param endDate The end date.
 * @param options The options to format the date in.
 * @returns The formatted date range.
 *
 * @example formatAsDateRangeWithYear('2021-01-01', '2021-01-02', { locale: 'en', timezone: 'UTC' }) // Jan 1, 2021 - Jan 2, 2021
 * @example formatAsDateRangeWithYear('2020-12-01', '2021-01-02', { locale: 'en', timezone: 'UTC' }) // Dec 1, 2020 - Jan 2, 2021
 * @example formatAsDateRangeWithYear('2021-01-01', '2021-01-02', { locale: 'fr', timezone: 'America/New_York' }) // 1 janv., 2021 - 2 janv., 2021
 * @example formatAsDateRangeWithYear('2020-12-01', '2021-01-02', { locale: 'fr', timezone: 'America/New_York' }) // 1 déc. 2020 - 2 janv., 2021
 */
export function formatAsShortDateRangeWithYears(startDate: DatesParam, endDate: DatesParam, { locale = 'en', timezone = 'UTC' }: FormatterOptions = {}) {
  return `${formatAsShortDateYear(startDate, { locale, timezone })} - ${formatAsShortDateYear(endDate, { locale, timezone })}`
}

/**
 * Format a date as a short date with time.
 *
 * @param date The date to format.
 * @param options The options to format the date in.
 * @returns The formatted date.
 *
 * @example formatAsShortDateWithTime('2021-01-01', { locale: 'en', timezone: 'UTC' }) // Jan 1, 2021 12:00 AM
 * @example formatAsShortDateWithTime('2021-01-01', { locale: 'fr', timezone: 'America/New_York' }) // 1 janv. 2021 00:00
 */
export function formatAsShortDateWithTime(date?: DatesParam, { locale = 'en', timezone = 'UTC' }: FormatterOptions = {}) {
  return datetime.utc(date).locale(locale).tz(timezone).format('lll')
}

/**
 * Format a date as a short date with year.
 *
 * @param date The date to format.
 * @param options The options to format the date in.
 * @returns The formatted date.
 *
 * @example formatAsShortDateYear('2021-01-01', { locale: 'en', timezone: 'UTC' }) // Jan 1, 2021
 * @example formatAsShortDateYear('2021-01-01', { locale: 'fr', timezone: 'America/New_York' }) // 1 janv. 2021
 */
export function formatAsShortDateYear(date?: DatesParam, { locale = 'en', timezone = 'UTC' }: FormatterOptions = {}) {
  return datetime.utc(date).locale(locale).tz(timezone).format('ll')
}

/**
 * Format a date as a time.
 *
 * @param date The date to format.
 * @param options The options to format the date in.
 * @returns The formatted date.
 *
 * @example formatAsTime('2021-01-01', { locale: 'en', timezone: 'UTC' }) // 12:00 AM
 * @example formatAsTime('2021-01-01', { locale: 'fr', timezone: 'America/New_York' }) // 00:00
 */
export function formatAsTime(date?: DatesParam, { locale = 'en', timezone = 'UTC' }: FormatterOptions = {}) {
  const formatter = locale === 'en' ? 'h:mm A' : 'HH:mm'
  return datetime.utc(date).locale(locale).tz(timezone).format(formatter)
}

/**
 * Format a date as a time zone.
 *
 * @param date The date to format.
 * @param options The options to format the date in.
 * @returns The formatted date.
 *
 * @example formatAsTimeZone('2021-01-01', { locale: 'en', timezone: 'UTC' }) // EST
 * @example formatAsTimeZone('2021-01-01', { locale: 'fr', timezone: 'America/New_York' }) // EST
 */
export function formatAsTimeZone(date?: DatesParam, { locale = 'en', timezone = 'UTC' }: FormatterOptions = {}) {
  return datetime.utc(date).locale(locale).tz(timezone).format('z')
}

/**
 * Format a date as a year only.
 *
 * @param date The date to format.
 * @param options The options to format the date in.
 * @returns The formatted date.
 *
 * @example formatAsYearOnly('2021-01-01', { locale: 'en', timezone: 'UTC' }) // 2021
 * @example formatAsYearOnly('2021-01-01', { locale: 'fr', timezone: 'America/New_York' }) // 2021
 */
export function formatAsYearOnly(date?: DatesParam, { locale = 'en', timezone = 'UTC' }: FormatterOptions = {}) {
  return datetime.utc(date).locale(locale).tz(timezone).format('YYYY')
}

/**
 * Translate a time to a formatted string.
 *
 * @param value The time to translate.
 * @param options The options to format the date in.
 * @param formatHourIsUppercase Whether to format the hour as uppercase.
 * @returns The formatted time.
 *
 * @example translateTime(9, { locale: 'en', timezone: 'UTC' }) // 9:00 AM
 * @example translateTime(9, { locale: 'fr', timezone: 'America/New_York' }) // 09:00
 */
export function translateTime(value: number, formatHourIsUppercase = true, { locale = 'en', timezone = 'UTC' }: FormatterOptions = {}) {
  const formatter = locale === 'en' ? `h:mm ${formatHourIsUppercase ? 'A' : 'a'}` : `HH:mm`
  return datetime().utc().hour(value).minute(0).second(0).locale(locale).tz(timezone).format(formatter)
}
